import React, { useEffect, useState } from 'react';
import { Storage, Translate, translate } from 'react-jhipster';
import { useAppDispatch } from 'app/config/store';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import TimerIcon from '@mui/icons-material/Timer';
import { Button } from 'reactstrap';
import FormHelperText from '@mui/material/FormHelperText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateEntity as updateBranch } from 'app/entities/branch/branch.reducer';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import styled from '@mui/styles/styled';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { AUTHORITIES } from 'app/config/constants';

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-input': {
    direction: 'ltr',
  },
});

interface BranchSettingsLocalProps {
  account: any;
  branchEntity: any;
  isUserAuthorizedToEdit: boolean;
}

const MINIMUM_TIME_LIMIT_MINUTES = 30;

export function BranchSettingsLocal({ account, branchEntity, isUserAuthorizedToEdit }: BranchSettingsLocalProps) {
  const dispatch = useAppDispatch();

  const [autoSellCollapse, setAutoSellCollapse] = useState(true);
  const [autoSellEnabled, setAutoSellEnabled] = useState(false);
  const [autoSellTimeLimit, setAutoSellTimeLimit] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Convert time limit to minutes for storage
  const convertToMinutes = (value: string): number => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue)) return 0;
    return numValue;
  };

  // Convert minutes back to display format
  const convertFromMinutes = (minutes: number): string => {
    if (minutes === 0) return '';
    return minutes.toString();
  };

  const meetsMinimumTimeLimit = (value: string): boolean => {
    if (!value || !/^\d+$/.test(value)) return false;

    const numValue = parseInt(value, 10);
    if (isNaN(numValue)) return false;
    return numValue >= MINIMUM_TIME_LIMIT_MINUTES;
  };

  const handleAutoSellCollapse = () => {
    setAutoSellCollapse(prev => !prev);
  };

  const handleAutoSellEnabledChange = event => {
    setAutoSellEnabled(event.target.checked);
    if (!event.target.checked) {
      setErrorMessage(null);
    } else if (!autoSellTimeLimit) {
      setErrorMessage(translate('comexApp.branch.autoSell.timeLimitRequired'));
    }
  };

  const handleAutoSellTimeLimitChange = event => {
    const value = event.target.value;
    setAutoSellTimeLimit(value);
    validateTimeLimit(value);
  };

  const validateTimeLimit = (value: string) => {
    if (autoSellEnabled && !value) {
      setErrorMessage(translate('comexApp.branch.autoSell.timeLimitRequired'));
    } else if (value && !/^\d+$/.test(value)) {
      setErrorMessage(translate('comexApp.branch.autoSell.invalidTimeLimitFormat'));
    } else if (value === '0') {
      setErrorMessage(translate('comexApp.branch.autoSell.timeLimitMustBeGreaterThanZero'));
    } else if (value && !meetsMinimumTimeLimit(value)) {
      setErrorMessage(
        translate('comexApp.branch.autoSell.timeLimitTooSmall', {
          minValue: <b>{MINIMUM_TIME_LIMIT_MINUTES}</b>,
        })
      );
    } else {
      setErrorMessage(null);
    }
  };

  const handleSubmit = () => {
    const timeLimitInMinutes = autoSellEnabled && autoSellTimeLimit ? convertToMinutes(autoSellTimeLimit) : null;

    const updatedBranch = {
      ...branchEntity,
      autoSellLocalCommodityEnabled: autoSellEnabled,
      autoSellLocalCommodityTimeLimit: timeLimitInMinutes,
    };

    dispatch(updateBranch(updatedBranch));
  };

  // Load initial values
  useEffect(() => {
    if (branchEntity?.id !== undefined) {
      if (branchEntity?.autoSellLocalCommodityEnabled !== undefined) {
        setAutoSellEnabled(branchEntity.autoSellLocalCommodityEnabled);
      }
      if (branchEntity?.autoSellLocalCommodityTimeLimit !== null && branchEntity?.autoSellLocalCommodityTimeLimit !== undefined) {
        const value = convertFromMinutes(branchEntity.autoSellLocalCommodityTimeLimit);
        setAutoSellTimeLimit(value);
      }
    }
  }, [branchEntity]);

  // Update error messages when locale changes
  useEffect(() => {
    if (errorMessage && autoSellTimeLimit) {
      validateTimeLimit(autoSellTimeLimit);
    }
  }, [Storage.session.get('locale')]);

  const isSaveDisabled =
    (account?.authorities?.includes(AUTHORITIES.ALSHMOKH_USER) && !account?.authorities?.includes(AUTHORITIES.SUBADMIN_BRANCH)) ||
    !isUserAuthorizedToEdit ||
    !!errorMessage ||
    (autoSellEnabled && !autoSellTimeLimit) ||
    (autoSellEnabled && autoSellTimeLimit && !meetsMinimumTimeLimit(autoSellTimeLimit));

  const formatMinutesToDisplay = (totalMinutes: number): string => {
    if (!totalMinutes || totalMinutes < 60) return '';

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (minutes === 0) {
      return `(${hours} ${translate('comexApp.branch.autoSell.hours')})`;
    } else {
      return `(${hours} ${translate('comexApp.branch.autoSell.hours')} ${minutes} ${translate('comexApp.branch.autoSell.minutesHelper')})`;
    }
  };

  return (
    <>
      {/* Auto Sell Collapse Section */}
      <div className="collapse-section">
        <Grid container className="align-items-center">
          <Grid item xs={10} sm={11} md={11.5}>
            <Typography variant="h6" component="div">
              {translate('comexApp.branch.settings.local.autoSell.title')}
            </Typography>
          </Grid>
          <Grid item xs={2} sm={1} md={0.5}>
            <IconButton aria-label="collapse" onClick={handleAutoSellCollapse}>
              {autoSellCollapse ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
            </IconButton>
          </Grid>
        </Grid>

        <Collapse in={autoSellCollapse}>
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff', borderRadius: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoSellEnabled}
                  onChange={handleAutoSellEnabledChange}
                  disabled={!isUserAuthorizedToEdit}
                  data-cy="autoSellLocalEnabled"
                />
              }
              label={translate('comexApp.branch.autoSell.enableAutoSell')}
              sx={{
                flexDirection: Storage.session.get('locale') === 'ar-ly' ? 'row-reverse' : 'row',
                marginLeft: Storage.session.get('locale') === 'ar-ly' ? 'auto' : 0,
                marginRight: Storage.session.get('locale') === 'ar-ly' ? 0 : 'auto',
              }}
            />

            {autoSellEnabled && (
              <>
                <Box
                  sx={{
                    mt: 2,
                    direction: Storage.session.get('locale') === 'ar-ly' ? 'rtl' : 'ltr',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      textAlign: Storage.session.get('locale') === 'ar-ly' ? 'right' : 'left',
                      color: 'text.secondary',
                    }}
                  >
                    {translate('comexApp.branch.autoSell.autoSellTimeLimit')}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StyledTextField
                        id="autoSellLocalTimeLimit"
                        name="autoSellLocalTimeLimit"
                        data-cy="autoSellLocalTimeLimit"
                        type="number"
                        size="small"
                        value={autoSellTimeLimit}
                        onChange={handleAutoSellTimeLimitChange}
                        disabled={!isUserAuthorizedToEdit}
                        required
                        error={!!errorMessage}
                        inputProps={{
                          min: MINIMUM_TIME_LIMIT_MINUTES,
                          step: 1,
                        }}
                        sx={{
                          backgroundColor: '#fff',
                          minWidth: '100px',
                          width: 'fit-content',
                          '& .MuiOutlinedInput-input': {
                            textAlign: Storage.session.get('locale') === 'ar-ly' ? 'right' : 'left',
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <TimerIcon color="action" fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />

                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.primary',
                        }}
                      >
                        {translate('comexApp.branch.autoSell.minutes', {
                          minValue: <b>{MINIMUM_TIME_LIMIT_MINUTES}</b>,
                        })}
                      </Typography>
                    </Box>

                    {autoSellTimeLimit && parseInt(autoSellTimeLimit, 10) >= 60 && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          alignSelf: 'center',
                        }}
                      >
                        {formatMinutesToDisplay(parseInt(autoSellTimeLimit, 10))}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {errorMessage && (
                  <FormHelperText
                    error
                    sx={{
                      textAlign: Storage.session.get('locale') === 'ar-ly' ? 'right' : 'left',
                      mt: 1,
                    }}
                  >
                    {errorMessage}
                  </FormHelperText>
                )}

                <FormHelperText
                  sx={{
                    textAlign: Storage.session.get('locale') === 'ar-ly' ? 'right' : 'left',
                    mt: 1,
                    fontWeight: 'bold',
                  }}
                >
                  {translate('comexApp.branch.autoSell.autoSellTimeLimitHelp')}
                </FormHelperText>
              </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" color="primary" onClick={handleSubmit} value={0} disabled={isSaveDisabled}>
                {translate('comexApp.bank.details.update')}
              </Button>
            </Box>
          </Box>
        </Collapse>
      </div>
    </>
  );
}
